import clsx from 'clsx'
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  ElementType,
  ReactNode
} from 'react'

type SectionProps<T extends ElementType = 'section'> = {
  as?: T
  children: ReactNode
  className?: string
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>

export function Section<T extends ElementType = 'section'>({
  as,
  children,
  className,
  ...restProps
}: SectionProps<T>): JSX.Element {
  const Component = as || 'section'

  return (
    <Component className={clsx('py-24', className)} {...restProps}>
      {children}
    </Component>
  )
}

export function SectionContainer({
  children,
  className,
  ...restProps
}: ComponentPropsWithoutRef<'div'>): JSX.Element {
  return (
    <div
      className={clsx('mx-auto w-full max-w-[72rem] px-4 sm:px-6', className)}
      {...restProps}
    >
      {children}
    </div>
  )
}

type HeadingTag = 'h1' | 'h2' | 'h3'
type SectionTitleSize = 'default' | 'hero'

const sectionTitleSizes: Record<SectionTitleSize, string> = {
  default:
    'text-[2rem] font-semibold tracking-[-0.02em] md:text-[3rem]',
  hero:
    'text-4xl font-bold leading-[0.95] tracking-[-0.045em] md:text-7xl'
}

type SectionTitleProps<T extends HeadingTag = 'h1'> = {
  as?: T
  children: ReactNode
  className?: string
  size?: SectionTitleSize
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>

export function SectionTitle<T extends HeadingTag = 'h1'>({
  as,
  children,
  className,
  size = 'default',
  ...restProps
}: SectionTitleProps<T>): JSX.Element {
  const Component = as || 'h1'

  return (
    <Component
      className={clsx(
        'text-[rgb(var(--ds-text))]',
        sectionTitleSizes[size],
        className
      )}
      {...restProps}
    >
      {children}
    </Component>
  )
}

type TextTag = 'p' | 'div' | 'span'

type SectionDescriptionProps<T extends TextTag = 'p'> = {
  as?: T
  children: ReactNode
  className?: string
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>

export function SectionDescription<T extends TextTag = 'p'>({
  as,
  children,
  className,
  ...restProps
}: SectionDescriptionProps<T>): JSX.Element {
  const Component = as || 'p'

  return (
    <Component
      className={clsx(
        'text-base leading-[1.6] text-[rgb(var(--ds-text-muted))]',
        className
      )}
      {...restProps}
    >
      {children}
    </Component>
  )
}

export function SurfaceCard({
  children,
  className,
  style,
  ...restProps
}: ComponentPropsWithoutRef<'div'>): JSX.Element {
  const surfaceStyles: CSSProperties = {
    borderColor: 'rgb(var(--ds-border))',
    ...style
  }

  return (
    <div
      className={clsx(
        'rounded-2xl border bg-[rgb(var(--ds-surface))] p-6',
        className
      )}
      style={surfaceStyles}
      {...restProps}
    >
      {children}
    </div>
  )
}
